import type { ScheduleTimeData } from "@qendo/schedule";
import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { Button } from "~/components/Button";
import { BUTTON_SIZE, ITEM_GAP, ScheduleTimeRow } from "~/components/form/schedule/ScheduleTimeRow";
import { useTheme } from "~/providers/ThemeProvider";

export type ScheduleTimeSelectorProps = {
    times?: ScheduleTimeData[] | null | undefined;
    disabled?: boolean;
    onChange?: (values: ScheduleTimeData[]) => void;
    dosageLabel?: string | null | undefined;
};

export const ScheduleTimeSelector = ({ times, disabled, onChange, dosageLabel }: ScheduleTimeSelectorProps) => {
    const { text } = useTheme();
    const entries = times ?? [];

    const [adding, setAdding] = useState(false);

    const remove = (id: keyof typeof entries) => {
        onChange?.(entries.filter((_skip, index) => index !== id).map(({ time, amount }) => ({ time, amount })));
    };

    const updateEntry = (id: number, value: ScheduleTimeData) => {
        const rows = [...entries];

        rows.splice(Math.min(rows.length, id), 1, value);

        onChange?.(rows);
    };

    const rows = entries.map((value, index) => ({
        key: `row-${index}`,
        value: value,
        onUpdate: (row: ScheduleTimeData) => updateEntry(index, row),
        onRemove: () => remove(index),
    }));

    const addEntry = (row: ScheduleTimeData) => {
        updateEntry(rows.length, row);
        setAdding(false);
    };

    return (
        <>
            {rows.map(({ key, ...props }) => (
                <ScheduleTimeRow key={key} {...props} disabled={disabled} dosageLabel={dosageLabel} />
            ))}
            {adding && (
                <ScheduleTimeRow
                    onUpdate={addEntry}
                    onRemove={() => setAdding(false)}
                    disabled={disabled}
                    dosageLabel={dosageLabel}
                />
            )}
            <View style={styles.newRow}>
                <Button
                    variant="success"
                    icon="plus"
                    size="sm"
                    onPress={() => setAdding(true)}
                    disabled={disabled || adding}
                />
                <Text style={[styles.addText, text.secondary]}>Add a time</Text>
            </View>
        </>
    );
};

const styles = StyleSheet.create({
    newRow: {
        flexDirection: "row",
        gap: ITEM_GAP,
        alignItems: "center",
    },
    button: {
        width: BUTTON_SIZE,
        height: BUTTON_SIZE,
        flexShrink: 0,
    },
    addText: {
        fontWeight: 400,
        fontSize: 14,
        lineHeight: 14,
    },
});
